// src/components/Rooms/RoomList.jsx
//
// Usa la misma fuente Fraunces que HowItWorks.jsx. Si ya agregaste el <link>
// de Google Fonts en public/index.html para ese componente, no necesitas
// hacer nada más aquí.

import React, { useEffect, useState } from 'react';
import Datepicker from "react-tailwindcss-datepicker";
import { useDispatch, useSelector } from 'react-redux';
import { fetchRooms } from '../../redux/slices/Rooms/roomsSlice';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../services/api';
import toast, { Toaster } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { usePricing, calculateLodgingPrice } from '../../utils/pricing';
import { todayMX } from '../../utils/dates';

const RoomList = () => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { rooms, isLoading, error } = useSelector((state) => state.rooms);
  const { user } = useSelector((state) => state.auth);
  const pricing = usePricing();

  const [filterType, setFilterType] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [availabilityMap, setAvailabilityMap] = useState({});
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [dateError, setDateError] = useState('');

  useEffect(() => {
    dispatch(fetchRooms());
  }, [dispatch]);

  // Estancia mínima: viene de macarios-api/config/pricing.js
  const minNights = pricing?.minNights ?? 2;

  const validateDates = (range) => {
    if (!range?.startDate || !range?.endDate) {
      setDateError('');
      return false;
    }
    const start = new Date(range.startDate);
    const end = new Date(range.endDate);
    const diffDays = (end - start) / (1000 * 60 * 60 * 24);
    if (diffDays < minNights) {
      setDateError(t('roomList.minStayError', { nights: minNights }));
      return false;
    }
    setDateError('');
    return true;
  };

  const handleDateRangeChange = (newRange) => {
    setDateRange(newRange);
    if (newRange.startDate && newRange.endDate) {
      validateDates(newRange);
    } else {
      setDateError('');
    }
  };

  // Calculate nights and total price (for 1 dog)
  const getNights = () => {
    if (!dateRange.startDate || !dateRange.endDate || dateError) return 0;
    const start = new Date(dateRange.startDate);
    const end = new Date(dateRange.endDate);
    return Math.ceil((end - start) / (1000 * 60 * 60 * 24));
  };

  const nights = getNights();
  // Tabla de precios del API (macarios-api/config/pricing.js); null mientras carga
  const estimatedTotal = calculateLodgingPrice(pricing, nights, 1);

  // Check availability when dates change and are valid
  useEffect(() => {
    if (!dateRange?.startDate || !dateRange?.endDate || dateError) {
      setAvailabilityMap({});
      return;
    }
    const timer = setTimeout(async () => {
      setCheckingAvailability(true);
      const newAvailability = {};
      const checkInDate = new Date(dateRange.startDate).toLocaleDateString('en-CA');
      const checkOutDate = new Date(dateRange.endDate).toLocaleDateString('en-CA');
      for (const room of rooms) {
        try {
          const res = await axios.get(`${API_BASE_URL}/bookings/check-availability`, {
            params: { roomId: room._id, checkInDate, checkOutDate },
          });
          newAvailability[room._id] = res.data.available;
        } catch {
          newAvailability[room._id] = false;
        }
      }
      setAvailabilityMap(newAvailability);
      setCheckingAvailability(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [dateRange, rooms, dateError]);

  // Filter rooms: by type and by availability (if dates selected)
  const filteredRooms = rooms.filter((room) => {
    if (filterType && room.type !== filterType) return false;
    const hasDates = dateRange.startDate && dateRange.endDate;
    if (hasDates && availabilityMap[room._id] === false) return false;
    return true;
  });

  const handleBookNow = (roomId) => {
    if (!user) {
      navigate('/login');
    } else {
      if (!validateDates(dateRange)) {
        toast.error(t('roomList.minStayError', { nights: minNights }));
        return;
      }
      let url = `/bookings/new?roomId=${roomId}`;
      if (dateRange.startDate) {
        const checkIn = new Date(dateRange.startDate).toLocaleDateString('en-CA');
        url += `&checkIn=${checkIn}`;
      }
      if (dateRange.endDate) {
        const checkOut = new Date(dateRange.endDate).toLocaleDateString('en-CA');
        url += `&checkOut=${checkOut}`;
      }
      navigate(url);
    }
  };

  const clearDates = () => {
    setDateRange({ startDate: null, endDate: null });
    setAvailabilityMap({});
    setDateError('');
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] bg-[#EFE6DD]">
        <div className="w-10 h-10 border-2 border-[#9A0002] border-t-transparent rounded-full animate-spin"></div>
        <p className="mt-4 text-sm tracking-wide text-[#3A2A20]/60">{t('common.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] bg-[#EFE6DD] px-4 text-center">
        <p className="text-[#9A0002] mb-4">{t('common.errorLoading')}: {error}</p>
        <button
          onClick={() => dispatch(fetchRooms())}
          className="px-6 py-2.5 bg-[#9A0002] text-[#EFE6DD] rounded-full text-sm font-medium hover:bg-[#7A0001] transition"
        >
          {t('common.tryAgain')}
        </button>
      </div>
    );
  }

  const hasDates = dateRange.startDate && dateRange.endDate;

  return (
    <>
      <Helmet>
        <title>{t('meta.rooms.title')}</title>
        <meta name="description" content={t('meta.rooms.description')} />
        <link rel="canonical" href="https://www.hotelmacarios.com.mx/rooms" />
      </Helmet>

      <div className="bg-[#EFE6DD] min-h-screen">
        <Toaster position="top-center" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
          {/* Header */}
          <div className="max-w-2xl mb-10">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9A0002]">
              {t('roomList.eyebrow') || t('roomList.subtitle')}
            </span>
            <h1 className="mt-2 font-['Fraunces'] text-4xl md:text-5xl font-semibold text-[#3A2A20]">
              {t('roomList.title')}
            </h1>
            <div className="mt-4 h-px w-24 border-t-2 border-dashed border-[#9A0002]/30" />
            <p className="mt-4 text-[#3A2A20]/70 leading-relaxed">{t('roomList.subtitle')}</p>
          </div>

          {/* Filters row */}
          <div className="flex flex-wrap gap-4 mb-3 p-4 sm:p-6 bg-white/60 border border-[#9A0002]/10 rounded-2xl items-end">
            <div className="flex-1 min-w-[240px]">
              <label className="block text-xs font-semibold uppercase tracking-wide text-[#3A2A20]/60 mb-2">
                {t('roomList.selectDates')}
              </label>
              <Datepicker
                value={dateRange}
                onChange={handleDateRangeChange}
                placeholder={t('roomList.datePlaceholder')}
                inputClassName="w-full px-4 py-3 text-base bg-white border border-[#3A2A20]/15 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#9A0002]/40 focus:border-[#9A0002] transition"
                primaryColor="red"
                useRange={true}
                asSingle={false}
                minDate={new Date(`${todayMX()}T00:00:00`)}
              />
            </div>
            <button
              onClick={clearDates}
              className="w-full sm:w-auto px-6 py-3 text-[#3A2A20] bg-transparent border border-[#3A2A20]/20 rounded-xl hover:bg-[#3A2A20]/5 transition font-medium text-sm"
            >
              {t('roomList.clearDates')}
            </button>
          </div>

          {/* Fixed height error area */}
          <div className="h-10 mb-1">
            {dateError && (
              <div className="px-4 py-2 bg-[#9A0002]/10 text-[#9A0002] rounded-lg text-center text-sm">
                {dateError}
              </div>
            )}
          </div>

          {/* Availability checking indicator */}
          {checkingAvailability && hasDates && !dateError && (
            <div className="mb-4 text-sm text-[#3A2A20]/60 flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-[#9A0002] border-t-transparent rounded-full animate-spin"></div>
              {t('roomList.checkingAvailability')}
            </div>
          )}

          {/* Room grid */}
          {filteredRooms.length === 0 ? (
            <div className="text-center py-16 bg-white/50 border border-[#9A0002]/10 rounded-2xl">
              <p className="text-[#3A2A20]/60">
                {hasDates && !dateError
                  ? t('roomList.noRoomsForDates')
                  : hasDates && dateError
                  ? t('roomList.invalidDates', { nights: minNights })
                  : t('roomList.noRoomsMatch')}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mt-6 sm:mt-8">
              {filteredRooms.map((room) => {
                const isBookable =
                  room.isActive && (!hasDates || availabilityMap[room._id] !== false) && !dateError;

                return (
                  <div
                    key={room._id}
                    className="group bg-white rounded-2xl overflow-hidden border border-[#3A2A20]/10 hover:border-[#9A0002]/30 shadow-sm hover:shadow-lg transition-all duration-300 flex flex-col"
                  >
                    <div className="aspect-[4/3] md:aspect-auto md:h-56 bg-[#9A0002] flex items-center justify-center overflow-hidden relative">
                      {room.images?.[0] ? (
                        <img
                          src={room.images[0]}
                          alt={room.name}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <span className="text-[#EFE6DD] font-['Fraunces'] text-lg">{room.name}</span>
                      )}
                    </div>

                    <div className="p-6 flex flex-col flex-grow">
                      <div className="flex justify-between items-start gap-3">
                        <h3 className="font-['Fraunces'] text-xl font-semibold text-[#3A2A20]">
                          {room.name}
                        </h3>
                        <span className="shrink-0 inline-block px-3 py-1 text-[11px] font-semibold uppercase tracking-wide rounded-full border border-[#9A0002]/30 text-[#9A0002]">
                          {room.type}
                        </span>
                      </div>
                      <p className="text-xs text-[#3A2A20]/50 mt-1">
                        {t('roomList.roomNumber')} {room.roomNumber}
                      </p>
                      <p className="text-sm text-[#3A2A20]/70 mt-3 leading-relaxed line-clamp-2">
                        {room.description}
                      </p>

                      <div className="mt-5 min-h-[32px] flex items-baseline flex-wrap gap-x-2 gap-y-1">
                        {hasDates && !dateError && estimatedTotal !== null && (
                          <span className="font-['Fraunces'] text-2xl font-semibold text-[#9A0002]">
                            ${estimatedTotal.toFixed(2)} <span className="text-sm font-sans font-normal text-[#3A2A20]/50">mxn</span>
                          </span>
                        )}
                      </div>

                      <button
                        onClick={() => handleBookNow(room._id)}
                        disabled={!isBookable}
                        className={`mt-5 w-full py-3 rounded-xl font-medium text-sm transition ${
                          isBookable
                            ? 'bg-[#9A0002] hover:bg-[#7A0001] text-[#EFE6DD] shadow-sm'
                            : 'bg-[#3A2A20]/10 text-[#3A2A20]/40 cursor-not-allowed'
                        }`}
                      >
                        {!room.isActive
                          ? t('roomList.notAvailable')
                          : dateError
                          ? t('roomList.invalidDatesShort')
                          : hasDates && availabilityMap[room._id] === false
                          ? t('roomList.notAvailableForDates')
                          : t('roomList.bookNow')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default RoomList;